import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    CardWrapper,
    SelectionCard,
    Wrapper,
    ScrollableContent,
    ActionSection,
    CombineInputGroup,
    ModalHeading,
    ModalSubHeading,
} from "../Styles/InputStyles";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import { MdAddLocationAlt, MdEdit } from "react-icons/md";
import SelectLocationModal from "../SelectLocationModal";
import SideModal from "../SideModal";
import AddLocation from "../Location/AddLocation";
import { FaPlus, FaUserPlus } from "react-icons/fa";
import tw from "tailwind-styled-components";
import { useSelector } from "react-redux";

const BasicDetails = ({
    onClose,
    addBasicDetailMutate,
    user,
    addBasicDetailLoading,
    setShowAddLocation,
}) => {
    const [showLocationModal, setShowLocationModal] = useState(false);
    const locations = useSelector((state) => state.LocationsReducer.locations);
    const filteredLocations = useMemo(() => {
        return locations?.filter(location => location.id !== 0) || [];
    }, [locations]);
    const initialValues = {
        propertyName: "",
        ownerContact: user.mobile,
        ownerName: user.name,
        propertyType: "",
        subAddress: "",
        numFloors: "",
        groundIncluded: false,
        numFlats: "",
        pinCode: "",
        locationId: "",
    };

    const propertyType = [
        { value: 1, label: "PG/Hostel/Co-Living" },
        { value: 2, label: "Flat/Apartment" },
    ];

    const validationSchema = Yup.object({
        propertyName: Yup.string()
            .trim()
            .required("Property Name is required")
            .matches(
                /^[a-zA-Z0-9\-]+(?: [a-zA-Z0-9\-]+)*$/,
                "Property Name should contain only alphabets, numbers, spaces, and dashes (-)",
            )
            .min(5, "Property Name must be at least 5 characters long"),
        ownerName: Yup.string()
            .trim()
            .required("Owner Name is required")
            .matches(
                /^[A-Za-z]+(?: [A-Za-z]+)*$/,
                "Owner Name should contain only alphabets",
            )
            .min(4, "Owner Name must be at least 4 characters long"),
        ownerContact: Yup.string()
            .required("Owner Contact is required")
            .matches(/^\d{10}$/, "Mobile number should be exactly 10 digits"),
        // subAddress: Yup.string().required("Address is required"),
        subAddress: Yup.string()
            .trim()
            .min(5, "Address must be at least 5 characters")
            .required("Address is required"),

        pinCode: Yup.string().matches(
            /^[1-9]\d{5}$/,
            "Pincode must be exactly 6 digits and not start with 0",
        ),
        propertyType: Yup.string().required("Property Type is required"),
        locationId: Yup.string().required("Location is required"),
        numFloors: Yup.number().when("propertyType", {
            is: 1,
            then: (schema) =>
                schema
                    .required("Number of Floors is required")
                    .min(1, "Number of Floors should be between 1 and 15")
                    .max(15, "Number of Floors should be between 1 and 15"),
            otherwise: (schema) => schema.notRequired(),
        }),
        numFlats: Yup.number().when("propertyType", {
            is: 2,
            then: (schema) =>
                schema
                    .required("Number of Flats is required")
                    .min(1, "Number of Flats should be between 1 and 100")
                    .max(100, "Number of Flats should be between 1 and 100"),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const handleSubmit = (values, { setSubmitting }) => {
        const submitValues = {
            ...values,
            pinCode: values.pinCode || null,
        };

        addBasicDetailMutate({
            propertyName: submitValues.propertyName,
            ownerMobile: submitValues.ownerContact,
            ownerName: submitValues.ownerName,
            type: submitValues.propertyType,
            address: submitValues.subAddress,
            pincode: submitValues.pinCode,
            locationId: submitValues.locationId,
            floorCount:
                submitValues.propertyType === 1 ? parseInt(submitValues.numFloors) : 0,
            isGroundIncluded: submitValues.groundIncluded ? 1 : 0,
            flatCount:
                submitValues.propertyType === 2 ? parseInt(submitValues.numFlats) : 0,
        });

        setSubmitting(false);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
        >
            {({
                values,
                setFieldValue,
                errors,
                touched,
                handleBlur,
                isSubmitting,
            }) => {
                const getLocationLabel = () => {
                    const selectedRole = filteredLocations.find(
                        (role) => role.id === values?.locationId,
                    );
                    return selectedRole ? selectedRole.name : "Select Location";
                };

                const handleLocationSelect = (selectedValue) => {
                    setFieldValue("locationId", selectedValue);
                    setFieldValue("locationTouched", true);
                };

                return (
                    <Form>
                        <ModalSubHeading>Step 1</ModalSubHeading>
                        <ModalHeading>Basic Details</ModalHeading>
                        <ScrollableContent>
                            <Wrapper>
                                <InputGroup>
                                    <Label htmlFor="propertyName">Property Name *</Label>
                                    <FieldWrappers>
                                        <Field
                                            type="text"
                                            name="propertyName"
                                            id="propertyName"
                                            autoComplete="off"
                                            required
                                        />
                                    </FieldWrappers>
                                    {touched.propertyName && errors.propertyName && (
                                        <p className="text-rose-600 text-sm mt-1">
                                            {errors.propertyName}
                                        </p>
                                    )}
                                </InputGroup>

                                <CombineInputGroup>
                                    <InputGroup>
                                        <Label htmlFor="ownerName">Owner's Name *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="ownerName"
                                                id="ownerName"
                                                autoComplete="off"
                                                required
                                            />
                                        </FieldWrappers>
                                        {touched.ownerName && errors.ownerName && (
                                            <p className="text-rose-600 text-sm mt-1">
                                                {errors.ownerName}
                                            </p>
                                        )}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="ownerContact">Owner's Contact *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="ownerContact"
                                                id="ownerContact"
                                                autoComplete="off"
                                                maxLength={10}
                                                required
                                            />
                                        </FieldWrappers>
                                        {touched.ownerContact && errors.ownerContact && (
                                            <p className="text-rose-600 text-sm mt-1">
                                                {errors.ownerContact}
                                            </p>
                                        )}
                                    </InputGroup>
                                </CombineInputGroup>




                                <InputGroup>
                                    <Label htmlFor="subAddress">Address *</Label>
                                    <FieldWrappers>
                                        <Field
                                            type="text"
                                            name="subAddress"
                                            id="subAddress"
                                            autoComplete="off"
                                            required
                                        />
                                    </FieldWrappers>
                                    {touched.subAddress && errors.subAddress && (
                                        <p className="text-rose-600 text-sm mt-1">
                                            {errors.subAddress}
                                        </p>
                                    )}
                                </InputGroup>

                                <InputGroup>
                                    <Label htmlFor="pinCode">Pin Code</Label>
                                    <FieldWrappers>
                                        <Field
                                            type="number"
                                            name="pinCode"
                                            id="pinCode"
                                            autoComplete="off"
                                            maxLength={6}
                                        />
                                    </FieldWrappers>
                                    {touched.pinCode && errors.pinCode && (
                                        <p className="text-rose-600 text-sm mt-1">
                                            {errors.pinCode}
                                        </p>
                                    )}
                                </InputGroup>
                                <InputGroup>
                                    <Label htmlFor="location">Select Location *</Label>
                                    <FieldWrappers onClick={() => setShowLocationModal(true)}>
                                        <Field
                                            type="text"
                                            name="locationId"
                                            id="locationId"
                                            autoComplete="off"
                                            readOnly
                                            value={getLocationLabel()}
                                            className="w-full border-none outline-none bg-transparent cursor-pointer"
                                        />
                                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                    </FieldWrappers>
                                    <div className="flex justify-between items-center">
                                        {touched.locationId && errors.locationId && (
                                            <p className="text-rose-600 text-sm mt-1">
                                                {errors.locationId}
                                            </p>
                                        )}
                                        <div className=" flex items-end justify-end ml-auto">
                                            <Location
                                                type="button"
                                                onClick={() => {
                                                    setShowAddLocation(true);
                                                }}
                                            >
                                                <MdAddLocationAlt className="mr-1 mt-0.5" />
                                                Add Location
                                            </Location>
                                        </div>
                                    </div>
                                </InputGroup>
                                <InputGroup>
                                    <Label>Property Type *</Label>
                                    <CardWrapper>
                                        {propertyType.map((mode) => (
                                            <SelectionCard
                                                key={mode.value}
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setFieldValue("numFloors", "");
                                                    setFieldValue("numFlats", "");
                                                    setFieldValue("propertyType", mode.value);
                                                    setTimeout(() => {
                                                        setFieldValue("propertyTypeTouched", true);
                                                    }, 100);
                                                }}
                                                className={
                                                    values.propertyType === mode.value
                                                        ? "bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 text-white"
                                                        : "bg-gray-100 text-gray-800"
                                                }
                                            >
                                                <span className="text-sm ">{mode.label}</span>
                                            </SelectionCard>
                                        ))}
                                    </CardWrapper>
                                    {touched.propertyType && errors.propertyType && (
                                        <p className="text-rose-600 text-sm mt-1">
                                            {errors.propertyType}
                                        </p>
                                    )}
                                </InputGroup>

                                {values.propertyType === 1 && (
                                    <>
                                        <InputGroup>
                                            <Label htmlFor="numFloors">No. of Floors *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="number"
                                                    name="numFloors"
                                                    id="numFloors"
                                                    min="1"
                                                    max="15"
                                                    value={values.numFloors}
                                                    onChange={(e) =>
                                                        setFieldValue("numFloors", e.target.value)
                                                    }
                                                    onBlur={handleBlur}
                                                    required
                                                />
                                            </FieldWrappers>
                                            {touched.numFloors && errors.numFloors && (
                                                <p className="text-rose-600 text-sm mt-1">
                                                    {errors.numFloors}
                                                </p>
                                            )}
                                        </InputGroup>
                                        <InputGroup>
                                            <label className="flex items-center space-x-2">
                                                <Field
                                                    className="form-checkbox h-4 w-4 text-blue-600"
                                                    type="checkbox"
                                                    name="groundIncluded"
                                                    checked={values.groundIncluded}
                                                    onChange={(e) =>
                                                        setFieldValue("groundIncluded", e.target.checked)
                                                    }
                                                />
                                                <span className="text-sm text-gray-500">
                                                    Ground Floor Included
                                                </span>
                                            </label>
                                        </InputGroup>
                                    </>
                                )}

                                {values.propertyType === 2 && (
                                    <InputGroup>
                                        <Label htmlFor="numFlats">No. of Flats *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="number"
                                                name="numFlats"
                                                id="numFlats"
                                                min="1"
                                                max="100"
                                                value={values.numFlats}
                                                onChange={(e) =>
                                                    setFieldValue("numFlats", e.target.value)
                                                }
                                                onBlur={handleBlur}
                                                required
                                            />
                                        </FieldWrappers>
                                        {touched.numFlats && errors.numFlats && (
                                            <p className="text-rose-600 text-sm mt-1">
                                                {errors.numFlats}
                                            </p>
                                        )}
                                    </InputGroup>
                                )}
                            </Wrapper>
                        </ScrollableContent>
                        <ActionSection>
                            <SideCancelBtn type="button" onClick={onClose}>
                                Cancel
                            </SideCancelBtn>
                            <SubmitBtn
                                type="submit"
                                disabled={addBasicDetailLoading || isSubmitting}
                            >
                                {addBasicDetailLoading ? "Processing..." : "Next"}
                            </SubmitBtn>
                        </ActionSection>

                        <SelectLocationModal
                            isOpen={showLocationModal}
                            onClose={() => setShowLocationModal(false)}
                            options={filteredLocations}
                            title="Select Location"
                            selectedValue={values.locationId}
                            onSelect={handleLocationSelect}
                            searchPlaceholder="Search Location..."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
const Location = tw.button`text-primary-color text-sm flex hover:underline`;
export default BasicDetails;
